import React from "react";
import styled from "@emotion/styled";

import * as tokens from "../../design-tokens";
import { Typography } from "../Typography";
import { SvgIcon, SvgNames } from "../../assets/icons/SvgIcon";
import { Button } from "../Button";
import { Skeleton } from "../../components/Skeleton";

function getFormattedDayAndMonth(strDate: string, timeZone?: string): string {
  const formattedFullDate = new Date(strDate).toLocaleTimeString("en-us", {
    timeZone,
    weekday: "short",
    day: "2-digit",
    month: "short",
    year: "numeric",
  });

  const [weekDay, month] = formattedFullDate.split(", ");

  return `${weekDay}, ${month}`;
}

const NavigationButton = ({
  direction,
  disabled,
  onNavigation,
}: {
  direction: "past" | "future";
  onNavigation: () => void;
  disabled?: boolean;
}) => {
  const isPastDirection = direction === "past";

  const iconName = isPastDirection
    ? SvgNames.ChevronLeft
    : SvgNames.ChevronRight;

  return (
    <Button
      color="tertiary"
      style={{ padding: 0 }}
      aria-label={`button-nav-${direction}`}
      disabled={disabled}
      onClick={() => onNavigation()}
      iconMiddle={
        <SvgIcon
          data-testid="testIcon"
          name={iconName}
          width={24}
          height={24}
          color={tokens.BRAND_HERO_BLUE}
        />
      }
    />
  );
};

const NavigationDate = ({
  direction,
  onNavigation,
  disabled,
  formattedDate,
  isLoading,
  dateText,
}) => (
  <DateWithNavigation direction={direction}>
    <NavigationButton
      aria-label={`${formattedDate}-navigate-${direction}`}
      direction={direction}
      onNavigation={onNavigation}
      disabled={disabled}
    />
    <DateHeaderText
      isLoading={isLoading}
      dateText={dateText}
      formattedDate={formattedDate}
    />
    <div />
  </DateWithNavigation>
);

const DateHeaderText = ({ isLoading, dateText, formattedDate }) =>
  isLoading ? (
    <StyledSkeleton key={formattedDate} />
  ) : (
    <Typography
      key={formattedDate}
      align="center"
      color={tokens.TINTS_OFF_WHITE_20}
    >
      {dateText}
    </Typography>
  );

const DateHeader = ({
  dates,
  disabledFutureNavigation,
  disabledPastNavigation,
  isLoading,
  onPrevious,
  onNext,
}: DateHeaderProps) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const formattedToday = getFormattedDayAndMonth(today.toISOString());
  const memoizedHeader = React.useMemo(
    () =>
      dates.map((day, index) => {
        const formattedDate = getFormattedDayAndMonth(`${day}T00:00`);
        const isToday = formattedDate === formattedToday;
        const dateText = isToday ? "Today" : formattedDate;

        if (dates.length === 1) {
          return (
            <DateWithNavigation direction="bidirectional">
              <NavigationButton
                aria-label={`${formattedDate}-navigate-past`}
                direction="past"
                onNavigation={onPrevious}
                disabled={isLoading || disabledPastNavigation}
              />
              <DateHeaderText
                isLoading={isLoading}
                dateText={dateText}
                formattedDate={formattedDate}
              />
              <NavigationButton
                aria-label={`${formattedDate}-navigate-future`}
                direction="future"
                onNavigation={onNext}
                disabled={isLoading || disabledFutureNavigation}
              />
            </DateWithNavigation>
          );
        }

        if (index === 0) {
          return (
            <NavigationDate
              key={`${formattedDate}-navigate-past`}
              direction="past"
              onNavigation={onPrevious}
              disabled={isLoading || disabledPastNavigation}
              formattedDate={formattedDate}
              isLoading={isLoading}
              dateText={dateText}
            />
          );
        }

        if (index === dates.length - 1) {
          return (
            <NavigationDate
              key={`${formattedDate}-navigate-future`}
              direction="future"
              onNavigation={onNext}
              disabled={isLoading || disabledFutureNavigation}
              formattedDate={formattedDate}
              isLoading={isLoading}
              dateText={dateText}
            />
          );
        }

        return (
          <DateHeaderText
            key={formattedDate}
            formattedDate={formattedDate}
            isLoading={isLoading}
            dateText={dateText}
          />
        );
      }),
    [
      dates,
      isLoading,
      onNext,
      disabledFutureNavigation,
      disabledPastNavigation,
      formattedToday,
      onPrevious,
    ]
  );

  return (
    <StyledDateHeader>
      <StyledGrid cols={dates.length}>{memoizedHeader}</StyledGrid>
    </StyledDateHeader>
  );
};

type DateHeaderProps = {
  dates: string[];
  disabledPastNavigation?: boolean;
  disabledFutureNavigation?: boolean;
  isLoading?: boolean;
  onPrevious: () => void;
  onNext: () => void;
};

const StyledDateHeader = styled.div`
  display: grid;
  border-top: 1px solid ${tokens.TINTS_OFF_WHITE_40};
  border-bottom: 1px solid ${tokens.TINTS_OFF_WHITE_40};
  padding-top: 16px;
  padding-bottom: 16px;
  margin-bottom: 16px;
`;

const StyledSkeleton = styled(Skeleton)`
  width: 56px;
  align-self: center;
  margin: auto;
`;

const DateWithNavigation = styled.div<{
  direction: "past" | "future" | "bidirectional";
}>`
  display: flex;
  flex-direction: ${(props) =>
    ["bidirectional", "past"].includes(props.direction)
      ? "row"
      : "row-reverse"};

  justify-content: space-between;
`;

const StyledGrid = styled.div<{ cols: number }>`
  display: grid;
  grid-template-columns: repeat(${(props) => props.cols}, 1fr);
  gap: 16px;
`;

const memoizedDateHeader = React.memo(DateHeader);

export type { DateHeaderProps };
export { memoizedDateHeader as DateHeader };
